import React, { useState } from "react";
import {
    Card,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Collapse,
    Divider,
    useMediaQuery,
    useTheme
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inbox";
import { useQueryWithStore } from "react-admin";
import { Link } from "react-router-dom";
import moment from "moment";

import CardIcon from "./CardIcon";

/**
 * Supply inventory alerts: lots at or past their expiry date, and supply types
 * that have fallen to their reorder point. Reads GET /supplyalerts.
 */
const useSupplyAlerts = () => {
    const { data, loaded } = useQueryWithStore({
        type: "getOne",
        resource: "supplyalerts",
        payload: { id: "current" },
    });

    return {
        loaded,
        expiring: (data && data.expiring) || [],
        lowStock: (data && data.low_stock) || [],
        total: (data && data.total) || 0,
    };
};

const ExpiringTable = ({ rows, theme }) => (
    <TableContainer
        component={Paper}
        sx={{ width: "100%", overflowX: "auto", mt: 1, boxShadow: 0 }}
    >
        <Typography sx={{ fontWeight: "bold", fontSize: 13, p: 1 }}>
            Expiring / expired lots
        </Typography>
        <Table size="small">
            <TableHead sx={{ backgroundColor: theme.palette.grey[300] }}>
                <TableRow>
                    <TableCell sx={{ fontWeight: "bold", fontSize: 13 }}>Supply Type</TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: 13 }}>Lot #</TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: 13 }}>Expiry Date</TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: 13 }}>Days</TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: 13 }}>On Hand</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={5} sx={{ fontSize: 13 }}>
                            Nothing expiring.
                        </TableCell>
                    </TableRow>
                )}
                {rows.map(row => (
                    <TableRow
                        key={row.id}
                        hover
                        sx={{
                            backgroundColor:
                                row.severity === "EXPIRED" ? "#ffe8e8" : "#fff6e0",
                        }}
                    >
                        <TableCell sx={{ fontSize: 13 }}>{row.supply_type}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>
                            <Link to={`/supplyreceipts/${row.id}/show`}>{row.lot_no}</Link>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>
                            {row.expiry_date ? moment(row.expiry_date).format("MM/DD/YYYY") : ""}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>
                            {row.severity === "EXPIRED"
                                ? "Expired"
                                : `${row.days_to_expire} day(s)`}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{row.qty_total}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const LowStockTable = ({ rows, theme }) => (
    <TableContainer
        component={Paper}
        sx={{ width: "100%", overflowX: "auto", mt: 2, boxShadow: 0 }}
    >
        <Typography sx={{ fontWeight: "bold", fontSize: 13, p: 1 }}>
            Low stock
        </Typography>
        <Table size="small">
            <TableHead sx={{ backgroundColor: theme.palette.grey[300] }}>
                <TableRow>
                    <TableCell sx={{ fontWeight: "bold", fontSize: 13 }}>Supply Type</TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: 13 }}>Available</TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: 13 }}>Reorder Point</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={3} sx={{ fontSize: 13 }}>
                            All supplies above their reorder point.
                        </TableCell>
                    </TableRow>
                )}
                {rows.map(row => (
                    <TableRow key={row.id} hover>
                        <TableCell sx={{ fontSize: 13 }}>
                            <Link
                                to={{
                                    pathname: "/supplylots",
                                    search: `filter=${JSON.stringify({
                                        supply_type_id: row.id,
                                    })}`,
                                }}
                            >
                                {row.supply_type}
                            </Link>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{row.qty_available}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{row.reorder_point}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const SupplyAlertCom = () => {
    const theme = useTheme();
    const isTablet = useMediaQuery("(max-width: 1024px)");
    const [isOpen, setIsOpen] = useState(false);
    const { loaded, expiring, lowStock, total } = useSupplyAlerts();

    if (!loaded) { return null; }

    const hasExpired = expiring.some(row => row.severity === "EXPIRED");
    const iconColor = hasExpired ? "red" : total > 0 ? "orange" : "navy";

    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: isTablet ? "90%" : 700,
                minWidth: 350,
                p: 0,
            }}
        >
            <Card
                sx={{
                    width: "100%",
                    minWidth: 350,
                    maxWidth: "100%",
                    borderRadius: 2,
                    boxShadow: 3,
                    p: 1,
                    overflow: "hidden",
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" gap={1}>
                        <CardIcon Icon={InventoryIcon} bgColor={iconColor} />
                        <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "14px" }}>
                            Inventory Alerts ({total})
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => setIsOpen(!isOpen)}
                        sx={{ fontSize: isTablet ? "24px" : "inherit" }}
                    >
                        {isOpen ? "▲" : "▼"}
                    </IconButton>
                </Box>
                <Collapse in={isOpen} sx={{ width: "100%" }}>
                    <Divider sx={{ my: 1 }} />
                    <ExpiringTable rows={expiring} theme={theme} />
                    <LowStockTable rows={lowStock} theme={theme} />
                </Collapse>
            </Card>
        </Box>
    );
};

export default SupplyAlertCom;
